import type { CustomDimensions } from '@de/types';
import type { EventContext } from './createEventingPayloadsDE';
import { ctaUrlForTelemetry } from './sanitizeOutboundUrlForTelemetry';

// Standalone PRQ (additional-questions) form identity. Shared by the
// `AdditionalQuestions` conversion-drawer `FormViewed` / per-question
// `FieldViewed` events and the debounced `FieldInputted` hook so the two
// surfaces can't drift. `dslConfigId` rides as a top-level field on the DSL
// path — the conversion-drawer `FormViewed` reads it; the field events ignore
// it (their registered schemas reject a top-level `customDimensions`). Absent
// on the imperative path, leaving those payloads byte-identical.
const PRQ_FORM_NAME = 'dexp-hosted-lead-form-additional';
const PRQ_STEP_NAME = 'additional-questions';

interface PrqFormEventContextArgs {
  eventContext: EventContext;
  productClickedCorrelationId?: string;
  dslConfigId?: string;
}

export const createPrqFormEventContext = ({
  eventContext,
  productClickedCorrelationId,
  dslConfigId,
}: PrqFormEventContextArgs): EventContext => ({
  ...eventContext,
  productClickedCorrelationId,
  formName: PRQ_FORM_NAME,
  stepName: PRQ_STEP_NAME,
  ...(dslConfigId ? { dslConfigId } : {}),
});

// Shared `webElement` identity for the governed DSL element events that ride
// the conversion drawer / thank-you page: the PRQ `ElementLoaded` +
// `ElementViewed` (`AdditionalQuestions`) and the application-CTA
// `ElementViewed` + `ElementClicked` (`ExitStrategy`). `dslConfigId` rides as
// a `customDimensions` entry on the DSL path so the element events correlate
// to the published config; absent on the imperative path so payloads stay
// byte-identical. `correlationId` and `productClickedCorrelationId` are both
// set to the supplied click-correlation id so `ElementLoaded` (reads
// `productClickedCorrelationId`) and `ElementViewed` / `ElementClicked` (read
// `correlationId`) resolve to the same value.
interface DslElementEventContextArgs {
  eventContext: EventContext;
  productClickedCorrelationId?: string;
  elementType: string;
  name: string;
  location: string;
  text?: string;
  dslConfigId?: string;
  /**
   * Interpolated Apply (`applicationCta`) or extra CTA (`postLeadCta`)
   * href. Stored as a sanitized `outboundUrl` customDimension so
   * ElementViewed / ElementClicked never carry lead-PII query params.
   */
  outboundUrl?: string;
  /**
   * CTA interpolator field map. Query keys whose source is lead PII
   * (`firstName`, `email`, `phone`, …) are stripped from `outboundUrl`.
   * Partner query-key names are not a signal.
   */
  fieldMap?: Record<string, string>;
}

export const createDslElementEventContext = ({
  eventContext,
  productClickedCorrelationId,
  elementType,
  name,
  location,
  text,
  dslConfigId,
  outboundUrl,
  fieldMap,
}: DslElementEventContextArgs): EventContext => {
  const customDimensions: CustomDimensions = [];
  if (dslConfigId) {
    customDimensions.push({ key: 'dslConfigId', value: dslConfigId });
  }
  const telemetryUrl = ctaUrlForTelemetry(outboundUrl, fieldMap);
  if (telemetryUrl) {
    customDimensions.push({ key: 'outboundUrl', value: telemetryUrl });
  }

  return {
    ...eventContext,
    correlationId: productClickedCorrelationId,
    productClickedCorrelationId,
    elementType,
    name,
    location,
    htmlId: eventContext.currentStep?.flowId,
    text: text ?? '',
    ...(customDimensions.length > 0 ? { customDimensions } : {}),
  };
};
